import { useMemo, type FC } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import { Button, DataTable } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useMintReportingDownload } from "../../../queries/regulator-portal/compliance/useMintReportingDownload";
import { useReportingRun } from "../../../queries/regulator-portal/compliance/useReportingRun";
import {
  groupReportingFiles,
  type ReportingRunFile,
} from "../../../queries/regulator-portal/compliance/parseReportingRuns";
import { triggerBrowserDownload } from "../../../utils/triggerBrowserDownload";
import {
  decodeRegulatorPortalPathId,
  REGULATOR_PORTAL_REPORTS_ROUTE,
} from "../regulatorPortalRoutes";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;

const RegulatorPortalReportDetail: FC = () => {
  const navigate = useNavigate();
  const { runId: rawRunId } = useParams<{ runId: string }>();
  const runId = decodeRegulatorPortalPathId(rawRunId ?? "");
  const runQuery = useReportingRun(runId || undefined);
  const mintDownload = useMintReportingDownload();
  const detail = runQuery.data;
  const groups = useMemo(
    () => groupReportingFiles(detail?.files ?? []),
    [detail?.files],
  );
  const loadError =
    runQuery.isError && !detail
      ? portalComplianceErrorMessage(
          runQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_REPORT_LOAD_ERROR,
        )
      : null;

  const handleDownload = async (file: ReportingRunFile) => {
    try {
      const download = await mintDownload.mutateAsync({
        runId,
        key: file.key,
      });
      triggerBrowserDownload(download.download_url);
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_DOWNLOAD_ERROR,
        ),
      );
    }
  };

  const fileColumns = [
    {
      title: AppStrings.PAM_REGULATOR_PORTAL_REPORT_DETAIL,
      dataIndex: "fileName",
      key: "fileName",
      render: (value: string, record: ReportingRunFile) =>
        emptyValue(value || record.key),
    },
    {
      title: AppStrings.DOWNLOAD,
      key: "download",
      render: (_value: unknown, record: ReportingRunFile) => (
        <Button
          onClick={() => handleDownload(record)}
          loading={
            mintDownload.isPending && mintDownload.variables?.key === record.key
          }
          data-testid={`regulator-portal-report-download-${record.key}`}
        >
          {AppStrings.DOWNLOAD}
        </Button>
      ),
    },
  ];

  return (
    <section className={styles.page} data-testid="regulator-portal-report-detail">
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate(REGULATOR_PORTAL_REPORTS_ROUTE)}
          aria-label={AppStrings.PAM_REGULATOR_PORTAL_BACK_TO_REPORTS}
          data-testid="regulator-portal-report-back"
        >
          <LeftOutlined className={styles.backButtonIcon} />
        </button>
        <h1 className={styles.title}>
          {AppStrings.PAM_REGULATOR_PORTAL_REPORT_DETAIL}
        </h1>
        <span className={styles.headerId}>{emptyValue(runId)}</span>
      </div>
      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {runQuery.isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}
      {!runQuery.isLoading && !loadError && !detail ? (
        <p className={styles.message}>
          {AppStrings.PAM_REGULATOR_PORTAL_REPORT_NOT_FOUND}
        </p>
      ) : null}
      {detail ? (
        <dl className={styles.details}>
          <div>
            <dt>{AppStrings.PAM_REGULATOR_PORTAL_REPORT_PACK}</dt>
            <dd>{emptyValue(detail.pack)}</dd>
          </div>
          <div>
            <dt>{AppStrings.PAM_REGULATOR_PORTAL_REPORT_BRAND}</dt>
            <dd>{emptyValue(detail.brand)}</dd>
          </div>
          <div>
            <dt>{AppStrings.PAM_REGULATOR_PORTAL_REPORT_REFERENCE_DATE}</dt>
            <dd>{emptyValue(detail.referenceDate)}</dd>
          </div>
          <div>
            <dt>{AppStrings.STATUS}</dt>
            <dd>{emptyValue(detail.status)}</dd>
          </div>
        </dl>
      ) : null}
      {detail && groups.length === 0 ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_FILES_EMPTY}</p>
      ) : null}
      {groups.map((group) => (
        <div
          key={group.grouping}
          className={styles.tableSection}
          data-testid={`regulator-portal-report-group-${group.grouping}`}
        >
          <h2 className={styles.groupTitle}>{group.label}</h2>
          <DataTable
            rowKey="key"
            columns={fileColumns}
            dataSource={group.files}
            pagination={false}
          />
        </div>
      ))}
    </section>
  );
};

export default RegulatorPortalReportDetail;
